import _ from "lodash";
import moment from "moment";
import { action, computed, decorate, observable, reaction } from "mobx";
import Clause from "models/Clause";
import dayjs from "dayjs";

class MatchupsDialogStore {
  constructor(
    statsApi,
    basicSearchStore,
    pitsMetadataStore,
    routerStore,
    videoSearchStore,
    sportsReferenceStore
  ) {
    this.statsApi = statsApi;
    this.basicSearchStore = basicSearchStore;
    this.pitsMetadataStore = pitsMetadataStore;
    this.routerStore = routerStore;
    this.videoSearchStore = videoSearchStore;
    this.sportsReferenceStore = sportsReferenceStore;

    this.date = new Date();
    this.selectedLeague = sportsReferenceStore.defaultSport;

    reaction(
      () => this.date,
      () => this.loadMatchups()
    );

    reaction(
      () => this.selectedLeague,
      () => this.loadMatchups()
    );

    reaction(
      () => this.sportsReferenceStore.sports.length,
      (length) => {
        if (length > 0) {
          this.selectedLeague = this.sportsReferenceStore.defaultSport;
        }
      }
    );

    this.initialize();
  }

  isOpen = false;
  schedules = observable.map();
  selected = observable.map();


  get matchups() {
    const schedule = this.schedules.get(this.selectedLeague) || {};
    const matchups = _.flatMap(schedule.dates, date => {
      return date.games.map(game => {
        const time = moment(game.gameDate).format("LT");
        const tz = new Date()
          .toLocaleTimeString("en-us", { timeZoneName: "short" })
          .split(" ")[2];
        const away = game.teams.away.team.abbreviation;
        const home = game.teams.home.team.abbreviation;
        return {
          label: `${away} @ ${home} - ${time} ${tz}`,
          value: game.gamePk.toString()
        };
      });
    });

    return {
      title: (
        this.sportsReferenceStore.sports.find(
          item => item.id === this.selectedLeague
        ) || { name: "Unknown" }
      ).name,
      id: this.selectedLeague,
      matchups
    };
  }

  initialize() {
    this.date = new Date();
    this.loadMatchups();
  }

  open() {
    this.isOpen = true;
  }

  close() {
    this.isOpen = false;
    this.selected.clear();
    this.date = new Date();
    this.selectedLeague = this.sportsReferenceStore.defaultSport;
  }

  addToQuery() {
    const gamePks = Array.from(this.selected.toJS().keys()).join(",");
    const clause = new Clause();
    const GAMEPK_FIELD_ID = 8;
    const {
      category,
      name,
      type,
      helperText,
      defaultOperator
    } = this.pitsMetadataStore.fields[GAMEPK_FIELD_ID];

    clause.fieldId = GAMEPK_FIELD_ID;
    clause.category = category;
    clause.fieldName = name;
    clause.type = type;
    clause.fieldHelperText = helperText;
    clause.operator = defaultOperator;
    clause.value = gamePks;

    this.basicSearchStore.clauses.replace([clause]);
    this.close();

    // Trigger a search with the updated query values.
    this.handleSearch();
  }

  addMatchup(gamePk) {
    this.selected.set(gamePk, true);
  }

  removeMatchup(gamePk) {
    this.selected.delete(gamePk);
  }

  selectAll() {
    this.matchups.matchups.forEach(matchup => {
      this.selected.set(matchup.value, true);
    });
  }

  unselectAll() {
    this.selected.clear();
  }

  setDate(date) {
    this.date = date;
  }

  addDay() {
    this.date = dayjs(this.date)
      .add(1, "day")
      .toDate();
  }

  subtractDay() {
    this.date = dayjs(this.date)
      .subtract(1, "day")
      .toDate();
  }

  setSelectedLeague(sportId) {
    this.selectedLeague = sportId;
  }

  loadMatchups() {
    const scheduleDate = dayjs(this.date).format("YYYY-MM-DD");

    this.sportsReferenceStore.sports.forEach(sport => {
      this.statsApi.getSchedule(scheduleDate, sport.id).then(data => {
        this.schedules.set(sport.id, data);
      });
    });
  }

  handleSearch() {
    this.videoSearchStore.resetPagination();
    if (!this.basicSearchStore.isValid()) {
      return;
    }

    this.routerStore.history.push({
      pathname: this.routerStore.location.pathname,
      search: `q=${this.basicSearchStore.pitsQuery}&fp=${this.videoSearchStore.feedPreference}`
    });
  }
}

decorate(MatchupsDialogStore, {
  isOpen: observable,
  selected: observable,
  schedule: observable,
  date: observable,
  selectedLeague: observable,
  matchups: computed,
  open: action,
  close: action,
  addMatchup: action,
  removeMatchup: action,
  selectAll: action,
  unselectAll: action,
  setDate: action,
  addDay: action,
  subtractDay: action,
  setSelectedLeague: action
});

export default MatchupsDialogStore;



// WEBPACK FOOTER //
// ./src/stores/MatchupsDialogStore.js